import apiClient from './apiClient'

/** Mensaje útil de una respuesta DRF (detail, error, o primer campo). */
function drfErrorMessage(data, fallback) {
  if (!data || typeof data !== 'object') return fallback
  if (typeof data.detail === 'string') return data.detail
  if (data.error) return String(data.error)
  for (const key of Object.keys(data)) {
    const v = data[key]
    if (Array.isArray(v) && v.length) return String(v[0])
    if (typeof v === 'string') return v
  }
  return fallback
}

/**
 * Subida genérica admin (multipart). Alineado con Jamrock: `subdirectory` o `folder`.
 * El apiClient quita Content-Type en FormData para que el boundary sea correcto.
 * Carpetas típicas: products, warranty_claims (ver `warranty.js` / formulario de garantías).
 */
export const uploadFile = async (file, options = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  const bucket = options.subdirectory ?? options.folder
  if (bucket) {
    formData.append('folder', bucket)
  }

  try {
    const response = await apiClient.post('/uploads/', formData, {
      timeout: Math.max(apiClient.defaults.timeout || 30000, 120000),
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type')
            headers.delete('content-type')
          } else if (headers) {
            delete headers['Content-Type']
            delete headers['content-type']
          }
          return data
        },
      ],
    })
    return response.data
  } catch (error) {
    throw new Error(
      drfErrorMessage(error.response?.data, error.message || 'Error al subir el archivo')
    )
  }
}

/**
 * Procesa una imagen de producto a WebP (≤ 200KB) en el servidor y la sube.
 * Devuelve el objeto de respuesta del backend:
 *   - subida:  { url, public_url, file_url, key, width, height, format, size_bytes }
 *   - preview: { preview: 'data:image/webp;base64,...', width, height, format, size_bytes }
 *
 * @param {File} file  Imagen original (jpeg/png/webp/gif).
 * @param {Object} options
 * @param {string|number} [options.productId]  Genera key products/{id}/{uuid}.webp
 * @param {boolean} [options.previewOnly]  true → no sube, devuelve base64
 */
export const processProductImage = async (file, options = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  if (options.productId != null && String(options.productId).trim() !== '') {
    formData.append('product_id', String(options.productId))
  }
  if (options.previewOnly) {
    formData.append('preview_only', 'true')
  }

  try {
    const response = await apiClient.post('/uploads/process-image/', formData, {
      timeout: Math.max(apiClient.defaults.timeout || 30000, 120000),
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type')
            headers.delete('content-type')
          } else if (headers) {
            delete headers['Content-Type']
            delete headers['content-type']
          }
          return data
        },
      ],
    })
    return response.data
  } catch (error) {
    throw new Error(
      drfErrorMessage(error.response?.data, error.message || 'Error al procesar la imagen')
    )
  }
}

export const getPublicCarouselImages = async () => {
  try {
    const response = await apiClient.get(`/uploads/carousel/public_carousel/?t=${Date.now()}`)
    return response.data
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    return []
  }
}

export const getCarouselImages = async () => {
  try {
    const response = await apiClient.get('/uploads/carousel/')
    const data = response.data
    if (Array.isArray(data)) {
      return data
    }
    if (data && Array.isArray(data.results)) {
      return data.results
    }
    return []
  } catch (error) {
    console.error('Error fetching carousel images:', error)
    throw new Error(error.response?.data?.message || 'Error al obtener las imágenes del carousel')
  }
}

export const updateCarouselImageStatus = async (id, isActive) => {
  try {
    const response = await apiClient.patch(
      `/uploads/carousel/${id}/`,
      { is_active: isActive },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error updating carousel image status:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el estado de la imagen')
  }
}

export const updateCarouselImageOrder = async (imageId1, newOrder1, imageId2, newOrder2) => {
  try {
    const [response1, response2] = await Promise.all([
      apiClient.patch(`/uploads/carousel/${imageId1}/`, { order: newOrder1 }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }),
      apiClient.patch(`/uploads/carousel/${imageId2}/`, { order: newOrder2 }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }),
    ])

    return { image1: response1.data, image2: response2.data }
  } catch (error) {
    console.error('Error updating carousel image order:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el orden de las imágenes')
  }
}

export const uploadCarouselImage = async (imageData) => {
  const formData = new FormData()

  if (imageData.title != null && String(imageData.title).trim() !== '') {
    formData.append('title', imageData.title)
  }
  if (imageData.description) formData.append('description', imageData.description)
  formData.append('link', imageData.link || '/productos')
  if (imageData.buttonText) formData.append('button_text', imageData.buttonText)
  formData.append('order', imageData.order ?? 0)
  formData.append('is_active', imageData.isActive !== undefined ? imageData.isActive : true)

  if (imageData.desktopImage) {
    formData.append('desktop_image', imageData.desktopImage)
  }
  if (imageData.mobileImage) {
    formData.append('mobile_image', imageData.mobileImage)
  }

  try {
    const response = await apiClient.post('/uploads/carousel/', formData, {
      timeout: Math.max(apiClient.defaults.timeout || 30000, 120000),
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    })
    return response.data
  } catch (error) {
    console.error('Error uploading carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al subir la imagen del carousel')
    )
  }
}

export const updateCarouselImage = async (id, imageData) => {
  try {
    const formData = new FormData()

    if (imageData.title != null && String(imageData.title).trim() !== '') {
      formData.append('title', imageData.title)
    }
    if (imageData.description) formData.append('description', imageData.description)
    if (imageData.link) formData.append('link', imageData.link)
    if (imageData.buttonText) formData.append('button_text', imageData.buttonText)
    if (imageData.order !== undefined) formData.append('order', imageData.order)
    if (imageData.isActive !== undefined) formData.append('is_active', imageData.isActive)

    if (imageData.desktopImage) {
      formData.append('desktop_image', imageData.desktopImage)
    }
    if (imageData.mobileImage) {
      formData.append('mobile_image', imageData.mobileImage)
    }

    const response = await apiClient.patch(`/uploads/carousel/${id}/`, formData, {
      timeout: Math.max(apiClient.defaults.timeout || 30000, 120000),
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    })

    return response.data
  } catch (error) {
    console.error('Error updating carousel image:', error)
    throw new Error(
      drfErrorMessage(error.response?.data, 'Error al actualizar la imagen')
    )
  }
}

export const deleteCarouselImage = async (id) => {
  try {
    const response = await apiClient.delete(`/uploads/carousel/${id}/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    })
    return response.data
  } catch (error) {
    console.error('Error deleting carousel image:', error)
    throw new Error(error.response?.data?.detail || 'Error al eliminar la imagen')
  }
}

export const getCarouselStatus = async () => {
  try {
    const response = await apiClient.get('/core/carousel-status/')
    return response.data
  } catch (error) {
    console.error('Error fetching carousel status:', error)
    return { carousel_enabled: true }
  }
}

export const updateCarouselStatus = async (enabled) => {
  try {
    const response = await apiClient.post(
      '/core/carousel-status/',
      { carousel_enabled: enabled },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error updating carousel status:', error)
    throw new Error(error.response?.data?.message || 'Error al actualizar el estado del carousel')
  }
}
